import createProps from '@kissui/helpers/src/props.helpers'
import { stringifyForAttribute, makeId } from '@kissui/helpers/src/utils'
import { EVENT_POPIN_OPEN, EVENT_QUICKVIEW_OPEN } from '@kissui/components'
import { dispatchEvent } from '@kissui/helpers/src/assets/js/eventDispatch'
import createRefs from '@kissui/helpers/src/refs.helpers'
import { getSkuComponentByType } from '@kissui/components/src/sku/services'
import { getProductAndClean } from '@kissui/helpers/src/catalog'
import { VARIATION_CROSS_SELL } from '@kissui/components/src/sku/constants'
import { getMergedProductData } from './dataTransform'
import { getPriceFormatter, market } from '@kissui/pdp-data'
import { NB_SLIDES_MOBILE, NB_SLIDES_TABLET, NB_SLIDES_DESKTOP } from './js/constants'
import { isLoggedIn } from '@kissui/helpers/src/dataLayer'
import fetcherService from './services/fetcher.service'
import {
    API_SOURCE_LAST_USER_ORDERS,
    API_SOURCE_COVEO_POPULAR_ITEMS_BOUGHT,
    API_SOURCE_COVEO_FREQUENTLY_BOUGHT_TOGETHER,
    API_SOURCE_HQ_RECOMMENDATION_HW
} from './constants'
import { getSkuQuickViewData } from '@kissui/components/src/sku-quick-view/shared-quick-view'
import { viewItemList } from '../../../helpers/src/gtmEvents'
class CrossSellNatural extends HTMLElement {
    constructor() {
        super()
        this.boundOnQuickViewOpen = this.onQuickViewOpen.bind(this)
        this.popinId = `quick_view_popin_${makeId(5)}`
    }

    connectedCallback() {
        this.props = createProps(this.attributes)
        this.moveBackground()
        this.onReady().then(() => {
            this.bindEvent()
            this.render()
        })

        const SKUs = this.props.products.map(product => product.sku)
        const { campaign, copywriting } = this.props

        viewItemList(SKUs, {
            listId: campaign.position,
            listName: `${campaign.position} - ${copywriting.heading}`,
            eventRaisedBy: campaign.position
        })
    }

    render() {
        if (this.currentProducts?.length === 0) {
            return
        }
        const { layout, campaign } = this.props

        this.innerHTML = `
            <nb-container
                campaign_id="${campaign.id}"
                campaign_name="${campaign.name}"
                campaign_position="${campaign.position}"
                campaign_creative="${campaign.creative}"
                contrast="${layout.contrast}"
                background_color="${layout.background_color}"
                background_mobile='${layout.background.mobile}'
                background_desktop='${layout.background.desktop}'
                background_retina='${layout.background.retina}'
                classname="${layout.padding_top} ${layout.padding_bottom} ${
            layout.background_color
        }">
                ${this.renderHeading()}
                ${this.renderDescription()}
                ${this.renderLink()}
                ${this.renderSlider()}
            </nb-container>
        `
        this.renderProducts()
    }

    moveBackground() {
        this.backgroundColor = this.querySelector('.cb-bg-color')
        this.backgroundImage = this.querySelector('.cb-bg-img')
        this.inner = this.querySelector('.cb-inner')
        this.slider = this.querySelector('nb-slider-natural')
        if (this.backgroundColor) {
            this.inner.insertBefore(this.backgroundColor, this.slider)
        }
        if (this.backgroundImage) {
            this.inner.insertBefore(this.backgroundImage, this.slider)
        }
    }

    renderHeading() {
        const { heading } = this.props.copywriting

        if (!heading) {
            return ''
        }

        return `<h2 class='h-3xl-300'>${heading}</h2>`
    }

    renderDescription() {
        const { description } = this.props.copywriting

        if (!description) {
            return ''
        }

        return `<p class='t-sm-400'>${description}</p>`
    }

    renderLink() {
        const { id, name, creative, position } = this.props.campaign
        const { link = {} } = this.props.copywriting
        const { label = '' } = link

        if (!label) {
            return ''
        }

        link.campaign_id = id
        link.campaign_name = name
        link.campaign_creative = creative
        link.campaign_position = position
        const data = stringifyForAttribute(link)
        return `<nb-link data="${data}">${label}</nb-link>`
    }

    renderSlider() {
        const { a11y_labels } = this.props.copywriting

        const data = {
            options: {
                mobile: {
                    items_per_slide: NB_SLIDES_MOBILE,
                    item_width: '279', // Need to check where this magic number comes from
                    slide_min_height: 'auto',
                    slider_gap: '16',
                    display_counter: 'true'
                },
                tablet: {
                    items_per_slide: NB_SLIDES_TABLET,
                    item_width: 'auto',
                    slide_min_height: 'auto',
                    slider_gap: '40'
                },
                desktop: {
                    items_per_slide: NB_SLIDES_DESKTOP,
                    item_width: 'auto',
                    slide_min_height: 'auto',
                    slider_gap: '40'
                }
            },
            a11y_labels
        }

        return `<nb-slider-natural data="${stringifyForAttribute(data)}" data-ref="slider">
            <li class="slide"><nb-loader></nb-loader></li>
            <li class="slide"><nb-loader></nb-loader></li>
            <li class="slide"><nb-loader></nb-loader></li>
        </nb-slider-natural>`
    }

    async onReady() {
        const payloads = await Promise.all([market(), getPriceFormatter(), fetcherService.init()])
        const [{ currency }, priceFormatter] = payloads
        this.currency = currency
        this.priceFormatter = priceFormatter
        await this.loadProducts()
    }

    async getExternal() {
        const { source } = this.props.options

        if (source === API_SOURCE_LAST_USER_ORDERS && isLoggedIn()) {
            return fetcherService.fetchProductsLastOrder()
        } else if (source === API_SOURCE_HQ_RECOMMENDATION_HW) {
            return fetcherService.fetchHQRecommendation()
        } else if (source === API_SOURCE_COVEO_POPULAR_ITEMS_BOUGHT) {
            return fetcherService.fetchCoveo(source)
        } else if (source === API_SOURCE_COVEO_FREQUENTLY_BOUGHT_TOGETHER) {
            const pdpProduct = await window
                .getPDPData()
                .then(v => v.configuration.eCommerceData.product.legacyId)
            return fetcherService.fetchCoveo(source, [pdpProduct])
        }
    }

    async loadProducts() {
        const { products } = this.props
        const external = await this.getExternal()

        this.currentProducts = external ?? products.map(product => product.sku)

        try {
            await getProductAndClean(this.currentProducts)
        } catch (e) {
            console.error(e)
        }
    }

    renderProducts() {
        this.refs = createRefs(this)
        const renderedProductList = this.currentProducts.reduce(
            (output, product, index) => `${output}${this.renderProductElement(product, index)}`,
            ''
        )

        this.slider = this.refs['slider']

        this.slider.carousel.innerHTML = `${renderedProductList}`
        this.slider.initSlider()
    }

    renderProductElement(product, index) {
        const { sku_quick_view, heading } = this.props.copywriting
        const { source } = this.props.options

        // TODO: DTO to merge and format data
        const tag = getSkuComponentByType(product) // sku-coffee
        const elementProduct = document.createElement(tag)

        const pageBuilderProductProps = this.props.products.find(
            productPageBuilder => productPageBuilder.sku === product.legacyId
        )

        const mergedProductData = getMergedProductData(
            product,
            pageBuilderProductProps ?? {},
            this.props,
            this.priceFormatter,
            this.currency
        )
        mergedProductData.variation = VARIATION_CROSS_SELL

        let trackingList = heading
        if (source) {
            trackingList = source
        }

        elementProduct.setAttribute('tracking_list', 'Cross Sell - ' + trackingList)
        elementProduct.setAttribute('tracking_position', index + 1)
        elementProduct.setAttribute('campaign_creative', this.props.campaign?.creative)

        if (
            mergedProductData.contact_cta?.contact_cta_text &&
            mergedProductData.contact_cta?.show_contact_cta
        ) {
            mergedProductData.show_view_details = true
        }

        if (sku_quick_view.more_info) {
            mergedProductData.quick_view_plp = {
                show_view: true,
                popin: {
                    popin_id: this.popinId,
                    close: sku_quick_view.popin.close,
                    footer: true
                }
            }

            mergedProductData.quick_view = {
                label: sku_quick_view.more_info,
                popin_id: this.popinId
            }
        }

        elementProduct.setAttribute('data', JSON.stringify(mergedProductData))
        return `<li class="slide">${elementProduct.outerHTML}</li>`
    }

    onQuickViewOpen(e) {
        const { detail = {} } = e
        const { sku_quick_view } = this.props.copywriting
        if (detail.id !== this.popinId || !detail.sku) {
            return
        }

        if (detail.sku_colors) {
            detail.sku_colors = detail.sku_colors.split(',')
        }

        const currentProduct = this.currentProducts.find(
            product =>
                product.legacyId === detail.sku ||
                detail.sku === product.internationalId ||
                (detail.sku_colors ? detail.sku_colors.indexOf(product.legacyId) : '')
        )
        const skuData = {
            ...this.props,
            priceFormatter: this.priceFormatter,
            currency: this.currency,
            sku_quick_view,
            tracking_position: detail.tracking_position,
            tracking_list: detail.tracking_list
        }
        getSkuQuickViewData(currentProduct, this.popinId, skuData, VARIATION_CROSS_SELL)
        dispatchEvent({ eventName: EVENT_POPIN_OPEN, args: { id: detail.id } })
    }

    bindEvent() {
        this.unbindEvent()
        window.addEventListener(EVENT_QUICKVIEW_OPEN, this.boundOnQuickViewOpen)
    }

    unbindEvent() {
        window.removeEventListener(EVENT_QUICKVIEW_OPEN, this.boundOnQuickViewOpen)
    }

    disconnectedCallback() {
        this.unbindEvent()
    }
}

customElements.get('nb-cross-sell-natural') ||
    customElements.define('nb-cross-sell-natural', CrossSellNatural)

export default CrossSellNatural
